import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import ProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { PricingTabType } from 'Services/ProductManagement/Features/Product/productSlice'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { TariffCatalogProductTabType } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import useTypedSelector from 'Store'
import { GridHighlightProvider } from 'Util/Context/gridHighlightContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import { IS_TARIFF_EDIT_ALLOWED, IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffCatalogProductDrawerProps {
  open: boolean
  handleClose: () => void
  handleProductDelete: (productId: string) => void
}

export function TariffCatalogProductDrawer({
  open,
  handleClose,
  handleProductDelete
}: Readonly<TariffCatalogProductDrawerProps>) {
  const { id: tariffId } = useParams()
  const { confirm } = useConfirm()
  const { singleProduct, singleLoading: singleProductLoading } = useTypedSelector(
    state => state.product
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const { singleTariff } = useSelectedTariff(tariffId)
  const { confirmForUnsavedChanges } = useUnsavedChanges()

  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })

  const productDetailsDrawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? TariffCatalogProductTabType.DETAILS

  const handleProductDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffCatalogProductTabType
  ) => {
    confirmForUnsavedChanges(() => {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      setSearchParams(searchParams)
    })
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      handleClose()
    })
  }

  /**
   * In Waltti environment only Exchange period is shown on tariff product level.
   * HSL environment has only Pricing enabled.
   */
  const allowedPricingTabs = [PricingTabType.PRICINGS]

  const isSingleProductLoading = singleProductLoading === 'pending'
  const showProductDeleteButton =
    singleProduct && IS_TARIFF_EDIT_ALLOWED && IS_WALTTI_SERVICE_ENVIRONMENT

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {showProductDeleteButton && (
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the product?`,
                  handleConfirm: () => {
                    handleProductDelete(singleProduct.id)
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
          )}
        </Box>
      }
    >
      <TabContext value={productDetailsDrawerTabFromParams}>
        <TabList
          onChange={handleProductDetailsDrawerTabChange}
          aria-label={t`Sales channel product tab list`}
        >
          <Tab label={t`Product details`} value={TariffCatalogProductTabType.DETAILS} />
          <Tab label={t`Pricing`} value={TariffCatalogProductTabType.PRICING} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.DETAILS}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductDetails
                product={singleProduct}
                vatDefinition={vatDefinition}
                isVatDefinitionsLoading={isVatDefinitionsLoading}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogProductTabType.PRICING}>
          <WithLoader loading={isSingleProductLoading}>
            <GridHighlightProvider>
              {singleProduct && (
                <ProductPricing product={singleProduct} allowedTabs={allowedPricingTabs} />
              )}
            </GridHighlightProvider>
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}

export default TariffCatalogProductDrawer
